import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Card, Table, Button, Spinner, Form, Dropdown, Pagination, Nav, Modal } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

// Custom Multi-Select Dropdown Component matching custom style
const MultiSelectDropdown = ({ title, options, selectedValues, onChange }) => {
  const handleToggle = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const allSelected = options.length > 0 && selectedValues.length === options.length;

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value ?? opt));
    }
  };

  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle 
        as="div"
        className="form-select w-100 text-start d-flex justify-content-between align-items-center" 
        style={{ 
          cursor: 'pointer', 
          borderColor: '#ced4da', 
          borderRadius: '4px', 
          height: '38px',
          padding: '0.375rem 2.25rem 0.375rem 0.75rem',
          backgroundColor: '#FFF'
        }}
      >
        <span className="text-truncate">
          {selectedValues.length === 0 
            ? `Tous/Toutes (${title})` 
            : `${selectedValues.length} sélectionné(s)`}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100 shadow-sm" style={{ maxHeight: '250px', overflowY: 'auto', padding: '6px' }}>
        <Dropdown.Item 
          as="button"
          onClick={handleToggleAll}
          className="d-flex justify-content-between align-items-center py-2 px-3 border-0 border-bottom mb-2 fw-bold"
          style={{
            backgroundColor: allSelected ? '#007BFF' : 'transparent',
            color: allSelected ? '#FFF' : '#333',
            fontSize: '0.88rem',
            borderRadius: '4px'
          }}
        >
          <span>Tous</span>
          {allSelected && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.78rem', color: '#FFF' }} />}
        </Dropdown.Item>
        {options.map((opt, index) => {
          const value = opt.value ?? opt;
          const label = opt.label ?? opt;
          const isChecked = selectedValues.includes(value);
          return (
            <Dropdown.Item 
              key={index}
              as="button"
              onClick={() => handleToggle(value)}
              className="d-flex justify-content-between align-items-center py-2 px-3 border-0"
              style={{
                backgroundColor: isChecked ? '#007BFF' : 'transparent',
                color: isChecked ? '#FFF' : '#333',
                fontSize: '0.88rem',
                borderRadius: '4px',
                marginBottom: '2px'
              }}
            >
              <span>{label}</span>
              {isChecked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.78rem', color: '#FFF' }} />}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default () => {
  const [tickets, setTickets] = useState([]);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal details state
  const [showModal, setShowModal] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Helper to get 1st of current month
  const getFirstOfMonth = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  };

  // Filters state
  const todayStr = new Date().toISOString().split('T')[0];
  const firstOfMonthStr = getFirstOfMonth();
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedDraws, setSelectedDraws] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Confirmed filter values for querying/display (Default to today)
  const [confirmedStart, setConfirmedStart] = useState(todayStr);
  const [confirmedEnd, setConfirmedEnd] = useState(todayStr);
  const [confirmedBranch, setConfirmedBranch] = useState("");
  const [confirmedAgents, setConfirmedAgents] = useState([]);
  const [confirmedDraws, setConfirmedDraws] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Fetch filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const agentsData = await client("agents/");
        const agentsList = agentsData ? (agentsData.results || (Array.isArray(agentsData) ? agentsData : [])) : [];
        setAgents(agentsList);

        const branchesData = await client("branches/");
        const branchesList = branchesData ? (branchesData.results || (Array.isArray(branchesData) ? branchesData : [])) : [];
        setBranches(branchesList);

        const drawsData = await client("draw-configs/");
        const drawsList = drawsData.draws || (Array.isArray(drawsData) ? drawsData : []);
        if (drawsList && drawsList.length > 0) {
          setDraws(drawsList.map(item => item.draw));
        } else {
          setDraws([
            { id: 1, name: "Florida matin" },
            { id: 2, name: "Florida soir" },
            { id: 3, name: "New-york matin" },
            { id: 4, name: "New-york soir" },
            { id: 5, name: "Georgia-Matin" }
          ]);
        }
      } catch (err) {
        console.error("Error loading report filters:", err);
      }
    };
    loadFilters();
  }, []);

  // Fetch report data
  const fetchReportTickets = useCallback(async () => {
    setLoading(true);
    try {
      let url = `reports/tickets/?type=winning&start_date=${confirmedStart}&end_date=${confirmedEnd}`;
      
      if (confirmedBranch) {
        const brObj = branches.find(b => b.name === confirmedBranch || b.id === confirmedBranch);
        if (brObj) url += `&branch=${brObj.id}`;
      }
      
      const data = await client(url);
      if (data) {
        setTickets(data);
      }
    } catch (err) {
      console.error("Error fetching report tickets:", err);
    } finally {
      setLoading(false);
    }
  }, [confirmedStart, confirmedEnd, confirmedBranch, branches]);

  useEffect(() => {
    fetchReportTickets();
    setCurrentPage(1);
  }, [fetchReportTickets]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleValidateFilters = () => {
    setConfirmedStart(startDate);
    setConfirmedEnd(endDate);
    setConfirmedBranch(selectedBranch);
    setConfirmedAgents(selectedAgents);
    setConfirmedDraws(selectedDraws);
    setCurrentPage(1);
  };

  // Ticket click to show modal details (Admin View)
  const handleTicketClick = async (ticketId) => {
    setLoadingDetails(true);
    setShowModal(true);
    try {
      const data = await client(`reports/tickets/${ticketId}/`);
      if (data) {
        setTicketDetails(data);
      }
    } catch (err) {
      console.error("Error loading ticket details:", err);
      alert("Impossible de charger les détails de la fiche.");
      setShowModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatTicketId = (ticketOrId) => {
    if (!ticketOrId) return "";
    if (typeof ticketOrId === "object") {
      if (ticketOrId.ticket_number && ticketOrId.ticket_number.length <= 15) {
        return ticketOrId.ticket_number.replace(/^0+/, "") || "0";
      }
      ticketOrId = ticketOrId.id || ticketOrId.ticket_number || "";
    }
    const str = String(ticketOrId).trim();
    const hex = str.replace(/-/g, "").toLowerCase();
    if (/^[0-9a-f]{16,32}$/.test(hex)) {
      try {
        const code = window.BigInt("0x" + hex).toString(36).toUpperCase().replace(/^0+/, "");
        return (code.length > 15 ? code.slice(0, 15) : code) || "0";
      } catch (e) {
        return hex.replace(/^0+/, "").toUpperCase() || "0";
      }
    }
    return str.replace(/-/g, "").replace(/^0+/, "").toUpperCase() || "0";
  };

  // Filter list locally based on multi-select filters and search bar
  const getFilteredTickets = () => {
    let list = tickets;

    if (confirmedAgents.length > 0) {
      list = list.filter(t => t.agent_name && confirmedAgents.includes(t.agent_name));
    }

    if (confirmedDraws.length > 0) {
      list = list.filter(t => t.draw_name && confirmedDraws.includes(t.draw_name));
    }

    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      list = list.filter(t => 
        formatTicketId(t).toLowerCase().includes(q) ||
        (t.id && t.id.toLowerCase().includes(q)) || 
        (t.agent_name && t.agent_name.toLowerCase().includes(q))
      );
    }

    return list;
  };

  const filteredTickets = getFilteredTickets();

  // Calculations for totals
  const totalSales = filteredTickets.reduce((acc, t) => acc + parseFloat(t.total_amount || 0), 0);
  const totalWinnings = filteredTickets.reduce((acc, t) => acc + parseFloat(t.potential_winnings || 0), 0);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const indexOfLastRow = currentPage * itemsPerPage;
  const indexOfFirstRow = indexOfLastRow - itemsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstRow, indexOfLastRow);

  // Formats date to YYYY-MM-DD HH:mm:ss
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const dt = new Date(dateStr);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    const s = String(dt.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d} ${h}:${min}:${s}`;
  };

  // Export handlers
  const handleCopy = () => {
    const headers = ["No Ticket", "Agent", "Tirage", "Date", "Vente", "Gain"];
    const rows = filteredTickets.map(t => [
      formatTicketId(t),
      t.agent_name || "",
      t.draw_name || "",
      formatDateTime(t.created_at),
      Math.round(parseFloat(t.total_amount) || 0),
      Math.round(parseFloat(t.potential_winnings) || 0)
    ]);
    const text = [headers.join("\t"), ...rows.map(r => r.join("\t"))].join("\n");
    navigator.clipboard.writeText(text);
    alert("Données copiées dans le presse-papiers !");
  };

  const handleExportExcel = () => {
    const headers = ["No Ticket", "Agent", "Tirage", "Date", "Vente", "Gain"];
    const rows = filteredTickets.map(t => [
      formatTicketId(t),
      t.agent_name || "",
      t.draw_name || "",
      formatDateTime(t.created_at),
      Math.round(parseFloat(t.total_amount) || 0),
      Math.round(parseFloat(t.potential_winnings) || 0)
    ]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `rapport_fiches_gagnantes_${confirmedStart}_to_${confirmedEnd}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter options list
  const filteredAgentOptions = agents
    .filter(a => !selectedBranch || a.branch_name === selectedBranch)
    .map(a => ({ value: a.username, label: a.username }));

  const drawOptions = draws.map(d => ({ value: d.name, label: d.name }));

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 className="mb-4" style={{ fontWeight: 'bold', color: '#222' }}>Rapport / Fiches gagnantes</h3>

      {/* Filters Card */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '20px' }}>
        <Row className="mb-3 align-items-end">
          {/* Dates */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Début</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Fin</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>

          {/* Succursale Filter */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Succursale</label>
            <Form.Select
              style={{ borderRadius: '4px', borderColor: '#CCC' }}
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setSelectedAgents([]); // Reset agents on branch change
              }}
            >
              <option value="">Toutes</option>
              {branches.map(b => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </Form.Select>
          </Col>

          {/* Agent Filter (Multi-select) */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Agent</label>
            <MultiSelectDropdown
              title="agents"
              options={filteredAgentOptions}
              selectedValues={selectedAgents}
              onChange={setSelectedAgents}
            />
          </Col>

          {/* Tirage Filter (Multi-select) */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Tirage</label>
            <MultiSelectDropdown
              title="tirages"
              options={drawOptions}
              selectedValues={selectedDraws}
              onChange={setSelectedDraws}
            />
          </Col>

          {/* Filter Button */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="d-none d-md-block">&nbsp;</label>
            <Button
              className="w-100"
              style={{
                backgroundColor: '#0069D9',
                borderColor: '#0062cc',
                color: '#FFF',
                fontWeight: '600',
                height: '38px',
                borderRadius: '4px'
              }}
              onClick={handleValidateFilters}
            >
              Filtrer
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="ms-2 text-muted small">Chargement des fiches...</span>
        </div>
      ) : (
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
          {/* Metadata Display */}
          <div className="mb-4 d-flex flex-wrap gap-4" style={{ fontSize: '0.95rem', color: '#333' }}>
            <div>Debut : <strong>{confirmedStart}</strong></div>
            <div>Fin : <strong>{confirmedEnd}</strong></div>
            <div>Total Fiche : <strong>{filteredTickets.length}</strong></div>
          </div>

          {/* Action Row */}
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: '10px' }}>
            <div className="d-flex" style={{ gap: '4px' }}>
              {["COPIER", "EXCEL", "PDF", "IMPRIMER"].map((label, idx) => (
                <button
                  key={idx}
                  style={{
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #CCCCCC',
                    color: '#333333',
                    fontSize: '0.78rem',
                    padding: '6px 16px',
                    borderRadius: '2px',
                    height: '34px',
                    fontWeight: 'normal',
                    outline: 'none'
                  }}
                  onClick={
                    label === "COPIER" ? handleCopy :
                    label === "EXCEL" ? handleExportExcel :
                    label === "IMPRIMER" ? () => window.print() :
                    () => alert(`${label} cliqué`)
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <span className="fw-bold small text-dark" style={{ fontSize: '0.92rem' }}>Search:</span>
              <Form.Control 
                type="text" 
                size="sm" 
                style={{ width: '220px', borderRadius: '4px', border: '1px solid #CCC' }} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Tickets Table */}
          <div className="table-responsive">
            <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
              <thead>
                <tr style={{ backgroundColor: '#F8F9FA' }}>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    No Ticket <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Tirage <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Date <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Vente <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Gain <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                </tr>
                {/* Total Row */}
                <tr style={{ fontWeight: 'bold', backgroundColor: '#FCFCFC', borderBottom: '2px solid #DDD', verticalAlign: 'middle' }}>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#333' }}>Total</td>
                  <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#333' }}>
                    {Math.round(totalSales || 0).toLocaleString('en-US')}
                  </td>
                  <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#333' }}>
                    {Math.round(totalWinnings || 0).toLocaleString('en-US')}
                  </td>
                </tr>
              </thead>
              <tbody>
                {/* Ticket Records */}
                {currentTickets.length > 0 ? (
                  currentTickets.map((t, idx) => (
                    <tr key={idx} style={{ verticalAlign: 'middle' }}>
                      <td 
                        style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#E64A19', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => handleTicketClick(t.id)}
                      >
                        {formatTicketId(t)}
                      </td>
                      <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#333' }}>
                        {t.agent_name || ""}
                      </td>
                      <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#555' }}>
                        {t.draw_name || ""}
                      </td>
                      <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', color: '#555' }}>
                        {formatDateTime(t.created_at)}
                      </td>
                      <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', fontWeight: '500' }}>
                        {Math.round(parseFloat(t.total_amount) || 0)}
                      </td>
                      <td style={{ fontSize: '0.8rem', padding: '12px 8px', textAlign: 'center', fontWeight: 'bold', color: '#28A745' }}>
                        {Math.round(parseFloat(t.potential_winnings) || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      Aucune fiche gagnante trouvée.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Card.Footer className="px-3 border-0 d-lg-flex align-items-center justify-content-between mt-3" style={{ backgroundColor: 'transparent' }}>
              <Nav>
                <Pagination className="mb-2 mb-lg-0">
                  <Pagination.Prev 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    Précédent
                  </Pagination.Prev>
                  {[...Array(totalPages).keys()].map(pageNumber => (
                    <Pagination.Item
                      key={pageNumber + 1}
                      active={currentPage === pageNumber + 1}
                      onClick={() => setCurrentPage(pageNumber + 1)}
                    >
                      {pageNumber + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  >
                    Suivant
                  </Pagination.Next>
                </Pagination>
              </Nav>
              <small className="fw-bold text-muted">
                Affichage de <b>{indexOfFirstRow + 1}</b> à <b>{Math.min(indexOfLastRow, filteredTickets.length)}</b> sur <b>{filteredTickets.length}</b> fiches
              </small>
            </Card.Footer>
          )}
        </Card>
      )}

      {/* Ticket Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: 'none' }}></Modal.Header>
        <Modal.Body className="px-5 pb-5">
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : ticketDetails ? (
            <div style={{ fontFamily: 'sans-serif', maxWidth: '800px', margin: '0 auto' }}>
              
              <div className="text-center mb-4">
                <div className="d-flex justify-content-center mb-2">
                  {ticketDetails.logo_url ? (
                    <img 
                      src={ticketDetails.logo_url} 
                      alt="Logo" 
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #222'
                      }} 
                    />
                  ) : (
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      border: '2.5px solid #222',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: '#222'
                    }}>
                      {ticketDetails.company_name ? ticketDetails.company_name.charAt(0).toUpperCase() : "B"}
                    </div>
                  )}
                </div>
                <h4 style={{ fontWeight: 'bold', color: '#111', textTransform: 'uppercase', margin: '0 0 6px 0', fontSize: '1.35rem' }}>
                  {ticketDetails.company_name || "Lotto Express Local"}
                </h4>

                <div style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#222' }}>
                  Tirage : {ticketDetails.draw_name}
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#222' }}>
                  {formatDateTime(ticketDetails.created_at)}
                </div>
              </div>

              {/* Status Header */}
              <div className="mb-4" style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#222' }}>
                Eliminer : {ticketDetails.status === "CANCELLED" ? (
                  <span className="text-danger">oui</span>
                ) : (
                  <span>non</span>
                )}
              </div>

              {/* POS / Ticket metadata table */}
              <div className="mb-4" style={{ border: '1px solid #B4D5FF', borderRadius: '2px', overflow: 'hidden' }}>
                <Row className="m-0 border-bottom" style={{ borderColor: '#B4D5FF' }}>
                  <Col xs={4} className="py-2 px-3 fw-bold" style={{ backgroundColor: '#FFF', borderRight: '1px solid #B4D5FF', color: '#555', fontSize: '0.92rem' }}>
                    POS :
                  </Col>
                  <Col xs={8} className="py-2 px-3" style={{ backgroundColor: '#FFF', color: '#0066cc', fontSize: '0.92rem', wordBreak: 'break-all' }}>
                    {ticketDetails.device_id || ""}
                  </Col>
                </Row>
                <Row className="m-0">
                  <Col xs={4} className="py-2 px-3 fw-bold" style={{ backgroundColor: '#FFF', borderRight: '1px solid #B4D5FF', color: '#555', fontSize: '0.92rem' }}>
                    # Ticket :
                  </Col>
                  <Col xs={8} className="py-2 px-3" style={{ backgroundColor: '#FFF', color: '#0066cc', fontSize: '0.92rem', wordBreak: 'break-all' }}>
                    {formatTicketId(ticketDetails)}
                  </Col>
                </Row>
              </div>

              {/* Table of items */}
              <div className="table-responsive mb-4" style={{ border: '1px solid #B4D5FF' }}>
                <Table bordered hover size="sm" className="mb-0" style={{ borderCollapse: 'collapse', border: 'none' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#D2E6FF' }}>
                      <th style={{ fontSize: '0.92rem', fontWeight: 'bold', color: '#111', padding: '10px 12px', border: '1px solid #B4D5FF' }}>Jeux</th>
                      <th style={{ fontSize: '0.92rem', fontWeight: 'bold', color: '#111', padding: '10px 12px', border: '1px solid #B4D5FF' }}>Pari</th>
                      <th style={{ fontSize: '0.92rem', fontWeight: 'bold', color: '#111', padding: '10px 12px', border: '1px solid #B4D5FF' }}>Mise</th>
                      <th style={{ fontSize: '0.92rem', fontWeight: 'bold', color: '#111', padding: '10px 12px', border: '1px solid #B4D5FF' }}>Gain</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ticketDetails.items || []).map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ fontSize: '0.9rem', color: '#333', padding: '8px 12px', border: '1px solid #B4D5FF' }}>{item.game_type}</td>
                        <td style={{ fontSize: '0.9rem', color: '#333', padding: '8px 12px', border: '1px solid #B4D5FF' }}>{item.numbers}</td>
                        <td style={{ fontSize: '0.9rem', color: '#333', padding: '8px 12px', border: '1px solid #B4D5FF' }}>{Math.round(parseFloat(item.amount) || 0)}</td>
                        <td style={{ fontSize: '0.9rem', color: '#28A745', padding: '8px 12px', border: '1px solid #B4D5FF', fontWeight: 'bold' }}>
                          {item.winning_amount && parseFloat(item.winning_amount) > 0 ? Math.round(parseFloat(item.winning_amount) || 0) : ""}
                        </td>
                      </tr>
                    ))}
                    {/* Total row matching screenshot */}
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#FFF' }}>
                      <td style={{ border: '1px solid #B4D5FF' }}></td>
                      <td style={{ border: '1px solid #B4D5FF' }}></td>
                      <td style={{ fontSize: '0.92rem', padding: '10px 12px', color: '#111', border: '1px solid #B4D5FF' }}>
                        Total Mise : {Math.round(parseFloat(ticketDetails.total_amount) || 0)}
                      </td>
                      <td style={{ fontSize: '0.92rem', padding: '10px 12px', color: '#28A745', border: '1px solid #B4D5FF', fontWeight: 'bold' }}>
                        Total Gain : {Math.round(parseFloat(ticketDetails.potential_winnings) || 0)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Action Button: Eliminer fiche */}
              {ticketDetails.status !== "CANCELLED" && (
                <div className="mt-4">
                  <Button 
                    variant="danger" 
                    className="w-100 fw-bold py-2 text-white" 
                    style={{ borderRadius: '4px', fontSize: '1rem', backgroundColor: '#DC3545', borderColor: '#DC3545' }}
                    onClick={async () => {
                      if (window.confirm("Êtes-vous sûr de vouloir éliminer (annuler) cette fiche ?")) {
                        try {
                          await client(`sales/tickets/${ticketDetails.id}/cancel/`, { method: "POST" });
                          alert("La fiche a été éliminée avec succès !");
                          setShowModal(false);
                          fetchReportTickets(); // Refresh list
                        } catch (err) {
                          console.error("Error canceling ticket:", err);
                          alert(err.message || "Erreur lors de l'annulation de la fiche.");
                        }
                      }
                    }}
                  >
                    Eliminer fiche
                  </Button>
                </div>
              )}

            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};
